// ============================================================================
// MODAL LOADING SKELETON
// ============================================================================
// Displays an animated skeleton placeholder that mimics detail-modal layouts
// (card sections + tables) during data loading, preventing blank screens.
// Reuses the skeleton-shimmer keyframes injected by GridLoadingSkeleton.

import React from 'react';

// Ensure shimmer keyframe exists (shared with GridLoadingSkeleton)
if (typeof document !== 'undefined') {
  const styleId = 'grid-loading-skeleton-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes skeleton-shimmer {
        0% { background-position: -400px 0; }
        100% { background-position: 400px 0; }
      }
      .grid-skeleton-bar {
        background: linear-gradient(90deg, var(--border-color, #e2e8f0) 25%, rgba(226,232,240,0.4) 50%, var(--border-color, #e2e8f0) 75%);
        background-size: 800px 100%;
        animation: skeleton-shimmer 1.5s infinite ease-in-out;
        border-radius: 4px;
        height: 14px;
      }
    `;
    document.head.appendChild(style);
  }
}

interface ModalLoadingSkeletonProps {
  /** Number of card sections to render (default 2) */
  sections?: number;
  /** Number of fields per card row (default 4) */
  fieldsPerRow?: number;
  /** Whether to render a table skeleton between sections (default true) */
  showTable?: boolean;
  /** Number of table rows (default 4) */
  tableRows?: number;
  /** Number of table columns (default 5) */
  tableCols?: number;
}

const Bar: React.FC<{ width: string; height?: number; delay?: number; style?: React.CSSProperties }> = ({
  width, height = 14, delay = 0, style,
}) => (
  <div
    className="grid-skeleton-bar"
    style={{
      width,
      height,
      animationDelay: delay ? `${delay.toFixed(2)}s` : undefined,
      ...style,
    }}
  />
);

const SectionSkeleton: React.FC<{ fieldsPerRow: number; delay: number }> = ({ fieldsPerRow, delay }) => (
  <div style={{
    marginBottom: '1.5rem',
    background: 'var(--bg-secondary)',
    padding: '1rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--border-color)',
  }}>
    {/* Section title */}
    <div style={{ marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--border-color)' }}>
      <Bar width="140px" height={12} delay={delay} />
    </div>
    {/* Field grid */}
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${fieldsPerRow}, 1fr)`, gap: '1rem' }}>
      {Array.from({ length: fieldsPerRow }).map((_, i) => (
        <div key={i}>
          <Bar width="60%" height={10} delay={delay + i * 0.05} style={{ marginBottom: '0.5rem' }} />
          <Bar width="80%" height={14} delay={delay + i * 0.05 + 0.03} />
        </div>
      ))}
    </div>
  </div>
);

const TableSkeleton: React.FC<{ rows: number; cols: number; delay: number }> = ({ rows, cols, delay }) => (
  <div style={{ marginBottom: '1.5rem' }}>
    {/* Table title */}
    <div style={{ marginBottom: '0.75rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--border-color)' }}>
      <Bar width="120px" height={12} delay={delay} />
    </div>
    {/* Table header */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: `40px repeat(${cols}, 1fr)`,
      gap: '0.75rem',
      padding: '0.5rem',
      background: 'var(--bg-tertiary)',
      borderBottom: '2px solid var(--border-color)',
    }}>
      <Bar width="20px" height={10} delay={delay + 0.02} />
      {Array.from({ length: cols }).map((_, i) => (
        <Bar key={i} width="70%" height={10} delay={delay + 0.02 + i * 0.03} />
      ))}
    </div>
    {/* Table rows */}
    {Array.from({ length: rows }).map((_, rowIdx) => (
      <div
        key={rowIdx}
        style={{
          display: 'grid',
          gridTemplateColumns: `40px repeat(${cols}, 1fr)`,
          gap: '0.75rem',
          padding: '0.5rem',
          borderBottom: '1px solid var(--border-color)',
        }}
      >
        <Bar width="16px" height={12} delay={delay + 0.1 + rowIdx * 0.06} />
        {Array.from({ length: cols }).map((_, colIdx) => (
          <Bar
            key={colIdx}
            width={colIdx === 0 ? '90%' : '60%'}
            height={12}
            delay={delay + 0.1 + rowIdx * 0.06 + colIdx * 0.03}
          />
        ))}
      </div>
    ))}
  </div>
);

const ModalLoadingSkeleton: React.FC<ModalLoadingSkeletonProps> = ({
  sections = 2,
  fieldsPerRow = 4,
  showTable = true,
  tableRows = 4,
  tableCols = 5,
}) => {
  let d = 0;
  const items: React.ReactNode[] = [];

  // First section card
  items.push(<SectionSkeleton key="s0" fieldsPerRow={fieldsPerRow} delay={d} />);
  d += 0.15;

  // Table (if enabled) after first section
  if (showTable) {
    items.push(<TableSkeleton key="t0" rows={tableRows} cols={tableCols} delay={d} />);
    d += 0.2;
  }

  // Remaining section cards
  for (let i = 1; i < sections; i++) {
    items.push(<SectionSkeleton key={`s${i}`} fieldsPerRow={fieldsPerRow} delay={d} />);
    d += 0.15;
  }

  return <>{items}</>;
};

export default ModalLoadingSkeleton;
